import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Shield, Zap, Target, Users, Award } from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Values.css';
import { ValuesHero } from '../components/PageHeroes';

const Values = () => {
  const values = [
    {
      icon: Shield,
      title: 'Trust & Security',
      description: 'We protect your data like it\'s our own, with bank-level encryption and rigorous compliance standards at every layer of the platform.'
    },
    {
      icon: Heart,
      title: 'Customer Obsession',
      description: 'Every feature we build starts with a real customer problem. Your success running payroll, finance, and HR is the only metric that matters to us.'
    },
    {
      icon: Zap,
      title: 'Relentless Innovation',
      description: 'We never stop improving. From automated calculations to real-time filing, we build the tools that keep you ahead, not just up to date.'
    },
    {
      icon: Target,
      title: 'Excellence in Everything',
      description: 'Payroll, tax, and financial accuracy leaves no room for error. We hold every part of the platform to the same exacting standard.'
    },
    {
      icon: Users,
      title: 'People First',
      description: 'Behind every number is a person — an employee waiting to be paid, a business owner trying to stay compliant. We never forget that.'
    },
    {
      icon: Award,
      title: 'Uncompromising Quality',
      description: 'We don\'t cut corners. Every feature is tested, every calculation verified, every submission checked before it reaches HMRC or your books.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      className="values-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ValuesHero />

      <section className="values-section">
        <div className="values-container">
         <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              textAlign: 'center',
              maxWidth: '820px',
              margin: '3rem auto',
              padding: '3rem 2.5rem',
              background: 'linear-gradient(135deg, #0f1d3a 0%, #16284f 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '1.25rem',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-80px',
                right: '-80px',
                width: '220px',
                height: '220px',
                background: 'radial-gradient(circle, rgba(13, 162, 124, 0.3) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />

            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'rgba(14, 165, 233, 0.18)',
                color: '#7dd3fc',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                padding: '0.4rem 1.1rem',
                borderRadius: '999px',
                marginBottom: '1.75rem',
                position: 'relative',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#2dd4bf',
                  display: 'inline-block',
                }}
              />
              Our Mission
            </div>

            <h2
              style={{
                fontSize: 'clamp(1.6rem, 3.2vw, 2.3rem)',
                fontWeight: 700,
                lineHeight: 1.4,
                margin: 0,
                color: '#f1f5f9',
                position: 'relative',
              }}
            >
              To make running a business{' '}
              <span style={{ color: '#2dd4bf' }}>
                — payroll, finance, and everything in between —
              </span>{' '}
              effortlessly simple, secure, and compliant for every UK company.
            </h2>

            <div
              style={{
                width: '56px',
                height: '3px',
                background: 'linear-gradient(90deg, #2dd4bf, #38bdf8)',
                borderRadius: '999px',
                margin: '2rem auto 0',
                position: 'relative',
              }}
            />
          </motion.div>

          <motion.div
            className="values-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={idx}
                  className="value-card"
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                >
                  <div className="value-icon-wrap">
                    <Icon size={24} />
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>

        <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              style={{
                textAlign: 'center',
                maxWidth: '640px',
                margin: '4rem auto 3rem',
                padding: '0 1.5rem',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '2px',
                  background: 'linear-gradient(90deg, #0da27c, #06b6d4)',
                  borderRadius: '999px',
                  margin: '0 auto 1.5rem',
                }}
              />
              <p
                style={{
                  fontSize: '1.05rem',
                  lineHeight: 1.75,
                  color: '#475569',
                  fontStyle: 'italic',
                  margin: 0,
                }}
              >
                These aren't just words on a wall. They're how we make decisions, hire our team, and
                build every part of <strong style={{ fontStyle: 'normal', color: '#0f1d3a' }}>Effective Global Staff</strong> — from
                payroll and invoicing to accounting and VAT.
              </p>
            </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Values;